import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Button, Checkbox, IconButton, Chip, Select, MenuItem,
    ListItemText, Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";

export default function MultiCompanyUserAssignDialog({
    open,
    onClose,
    onConfirm,
    currentCompanyCode,
    companies,
    userRolesByCompany,
    loginCompanyRoleNames = [],
    baseUserPayload,
    loading = false,
    selectedRolesByCompany = {}
}) {
    const loginRoleNameSet = useMemo(() => {
        const raw = loginCompanyRoleNames ?? [];
        const arr = Array.isArray(raw) ? raw : [raw];
        return new Set(arr.filter(Boolean).map(s => String(s).trim().toLowerCase()));
    }, [loginCompanyRoleNames]);

    const selectableCompanies = useMemo(
        () => (companies || []).filter(c => Number(c.companyCode) !== Number(currentCompanyCode)),
        [companies, currentCompanyCode]
    );

    const [rows, setRows] = useState([]);
    const [openRowId, setOpenRowId] = useState(null);

    useEffect(() => {
        if (!open) return;

        const isUpdate = baseUserPayload && baseUserPayload.isNew === false;

        const init = (selectableCompanies || []).map((c, idx) => {
            const options = (userRolesByCompany && userRolesByCompany[c.companyCode]) || [];

            let preselected = [];
            if (isUpdate) {
                const selectedIds =
                    (selectedRolesByCompany && selectedRolesByCompany[c.companyCode]) || [];
                if (Array.isArray(selectedIds) && selectedIds.length > 0) {
                    preselected = options.filter(opt => selectedIds.includes(opt.roleId));
                }
            } else {
                preselected = options.filter(
                    opt => loginRoleNameSet.has(String(opt.roleName).trim().toLowerCase())
                );
            }

            return {
                id: idx + 1,
                companyCode: c.companyCode,
                companyName: c.companyName,
                selected: true,
                roles: preselected
            };
        });

        setRows(init);
    }, [
        open,
        selectableCompanies,
        userRolesByCompany,
        loginRoleNameSet,
        selectedRolesByCompany,
        baseUserPayload
    ]);

    const allChecked = rows.length > 0 && rows.every(r => !!r.selected);
    const someChecked = rows.some(r => !!r.selected) && !allChecked;

    const columns = [
        {
            field: "selected",
            headerName: "",
            width: 64,
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => (
                <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={(e) => {
                        const checked = e.target.checked;
                        setRows(prev => prev.map(r => ({ ...r, selected: checked })));
                    }}
                />
            ),
            renderCell: (params) => (
                <Checkbox
                    checked={!!params.row.selected}
                    onChange={(e) => {
                        const checked = e.target.checked;
                        setRows(prev =>
                            prev.map(r => (r.id === params.row.id ? { ...r, selected: checked } : r))
                        );
                    }}
                />
            )
        },
        {
            field: "companyName",
            headerName: "Company",
            flex: 2,
            minWidth: 420,
            disableColumnMenu: true
        },
        {
            field: "roles",
            headerName: "User Group",
            flex: 1.2,
            minWidth: 340,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const options = (userRolesByCompany && userRolesByCompany[params.row.companyCode]) || [];
                const selectedRoles = params.row.roles || [];

                return (
                    <Box
                        sx={{
                            width: 'calc(100% + var(--DataGrid-cellPaddingX) * 2)', // removes internal padding
                            ml: 'calc(var(--DataGrid-cellPaddingX) * -1)',
                            mr: 'calc(var(--DataGrid-cellPaddingX) * -1)',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Select
                            multiple
                            size="small"
                            displayEmpty
                            fullWidth
                            sx={{
                                height: '100%',
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: 1,
                                    pr: 3,
                                },
                            }}
                            value={selectedRoles.map(r => r.roleId)}
                            onChange={(e) => {
                                const selectedIds = e.target.value;
                                const newRoles = options.filter(opt => selectedIds.includes(opt.roleId));
                                setRows(prev =>
                                    prev.map(r =>
                                        r.id === params.row.id
                                            ? { ...r, roles: newRoles, selected: newRoles.length > 0 || r.selected }
                                            : r
                                    )
                                );
                            }}
                            renderValue={(selected) => {
                                if (!selected || selected.length === 0)
                                    return <span style={{ color: '#aaa' }}>Select user group</span>;

                                const names = options
                                    .filter(opt => selected.includes(opt.roleId))
                                    .map(opt => opt.roleName);
                                const text = names.join(', ');

                                return (
                                    <Tooltip title={text}>
                                        <Box
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {text}
                                        </Box>
                                    </Tooltip>
                                );
                            }}

                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        minWidth: '100%',
                                        maxWidth: '100%',
                                    },
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                                marginThreshold: 0,
                            }}
                        >
                            {options.map((option) => (
                                <MenuItem key={option.roleId} value={option.roleId}>
                                    <Checkbox checked={selectedRoles.some(r => r.roleId === option.roleId)} />
                                    <ListItemText primary={option.roleName} />
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                );
            }


        }
    ];

    const handleSave = () => {
        const picked = rows.filter(r => r.selected);

        const invalidCompanies = picked.filter(r => !r.roles || r.roles.length === 0);
        if (invalidCompanies.length > 0) {
            const names = invalidCompanies.map(c => c.companyName).join(", ");
            toast.error(`Please assign at least one user group for ${names}`);
            return;
        }

        const userCompanyLists = picked.map(r => ({
            companyCode: r.companyCode,
            companyName: r.companyName,
            roles: (r.roles || []).map(x => ({
                roleId: x.roleId,
                roleName: x.roleName
            }))
        }));

        onConfirm({ ...baseUserPayload, userCompanyLists });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Add User to Other Companies
                <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers onClick={() => setOpenRowId(null)}>
                <Box sx={{ height: 440 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(r) => r.id}
                        disableRowSelectionOnClick
                        density="compact"
                        loading={loading}
                        disableColumnMenu
                        disableColumnFilter
                        disableColumnSelector
                        showColumnVerticalBorder
                        showCellVerticalBorder
                        hideFooter
                        sx={{
                            '--DataGrid-cellPaddingY': '10px',
                            '--DataGrid-cellPaddingX': '12px',
                            '& .MuiDataGrid-cell': { alignItems: 'center' },
                            '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid rgba(0,0,0,0.12)' }
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
